import { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useAdviserIdentity } from "@/hooks/useAdviserIdentity";

// Phase 3 (F6): /adviser entry.
//
// This route is intentionally NOT wrapped in `allowAdviser` — it is the landing that a
// non-adviser is redirected TO. It handles four cases:
//   - not authenticated        → go to /adviser/login
//   - staff member             → they don't belong in the adviser portal; nudge to /partner/dashboard
//   - confirmed adviser        → land them on /adviser/insights
//   - authed, no adviser link  → a calm "your access isn't set up yet" explainer
export default function AdviserHome() {
  const navigate = useNavigate();
  const { user, session, staffMember, loading: authLoading } = useAuth();
  const adviser = useAdviserIdentity();

  const isAuthenticated = !!user && !!session;

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/adviser/login", { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  if (authLoading || adviser.loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // The effect will redirect; render nothing meaningful in the meantime.
    return null;
  }

  // A confirmed external adviser lands on their default surface.
  if (adviser.isAdviser) {
    return <Navigate to="/adviser/insights" replace />;
  }

  // Staff shouldn't be in the adviser portal — point them home.
  if (staffMember) {
    return (
      <div className="max-w-lg mx-auto py-12">
        <Card>
          <CardHeader>
            <CardTitle>You're signed in as a team member</CardTitle>
            <CardDescription>
              The adviser portal is for external advisers. Head to your partner dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/partner/dashboard", { replace: true })}>
              Go to Partner Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Authenticated, but no adviser link yet — access hasn't been provisioned.
  return (
    <div className="max-w-lg mx-auto py-12">
      <Card>
        <CardHeader>
          <CardTitle>Your adviser access isn't set up yet</CardTitle>
          <CardDescription>
            You're signed in, but this account hasn't been linked to an adviser firm. If you were
            expecting access, please contact your CBAM relationship manager to be invited.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex gap-3">
          <Button variant="outline" onClick={() => navigate("/", { replace: true })}>
            Back to main site
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
